import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { cockpitStatisticsWidgets } from '~/config/widgets/cockpit/cockpitStatisticsWidgets';
import {
  EmptyBlock,
  fmtDate,
  Ico,
  LineChart,
  PageHead,
  V3Page,
  type IconName,
} from '~/components/cockpit/v3';
import {
  filterWeeklyBuckets,
  weekStartDateString,
  type WeeklyBucket,
} from '~/util/statisticsChart';
import type { WidgetProps } from '~/components/cockpit/cockpitData';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_STATISTICS,
)({
  component: StatisticsPage,
});

const METRICS: ReadonlyArray<{
  readonly key: string;
  readonly od: string;
  readonly icon: IconName;
  readonly tk: string;
  readonly label: string;
}> = [
  {
    key: 'cockpitStatisticsAdViews',
    od: 'stat-pageViews',
    icon: 'gauge',
    tk: 'cockpit_stats_views',
    label: 'Inseratsaufrufe',
  },
  {
    key: 'cockpitStatisticsDigitalTourViews',
    od: 'stat-tour360Views',
    icon: 'map',
    tk: 'cockpit_stats_tour',
    label: '360°-Rundgang',
  },
  {
    key: 'cockpitStatisticsExposeDownloadsViews',
    od: 'stat-exposeDownloads',
    icon: 'doc',
    tk: 'cockpit_stats_expose',
    label: 'Exposé-Downloads',
  },
];

function StatisticsPage(): React.ReactElement {
  const params = useAppParams(Route);
  const { t } = useTranslate();
  const [
    trendKey,
    setTrendKey,
  ] = useState('cockpitStatisticsAdViews');

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_STATISTICS,
    { params },
  );

  if (query.isLoading) return <LoadingWidget />;
  if (query.isError || !query.data) return <ErrorWidget error={query.error} />;

  if (!query.data.v3Redesign) {
    const widgetProps = query.data?.widgetProps || {};
    const widgets = cockpitStatisticsWidgets(widgetProps);
    return (
      <div className="h-full flex flex-col gap-y-6">
        <RenderWidgets widgets={widgets} />
      </div>
    );
  }

  const wp = (query.data.widgetProps || {}) as WidgetProps;
  const head = (
    <PageHead
      odId="page-head"
      title={t('cockpit_stats_title', 'Statistik')}
      sub={t('cockpit_stats_sub', 'Kennzahlen der Vermarktung')}
    />
  );
  const present = METRICS.filter((m) => wp[m.key]);

  if (present.length === 0) {
    return (
      <V3Page>
        {head}
        <EmptyBlock
          icon="chart"
          title={t('cockpit_stats_empty_title', 'Noch keine Statistik')}
          text={t(
            'cockpit_stats_empty_text',
            'Ihr Objekt ist noch nicht online. Ab dem ersten Tag der Ausschreibung sehen Sie hier Aufrufe, Downloads, Anfragen und Angebote – Woche für Woche.',
          )}
        />
      </V3Page>
    );
  }

  const label = (m: (typeof METRICS)[number]): string => t(m.tk, m.label);
  const weeks = (key: string): WeeklyBucket[] =>
    filterWeeklyBuckets(wp[key]?.lineChartData ?? [], 'all');
  const total = new Map<string, number>();
  present.forEach((m) =>
    weeks(m.key).forEach((b) =>
      total.set(b.week, (total.get(b.week) ?? 0) + b.count),
    ),
  );
  const totalBuckets: WeeklyBucket[] = [...total.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(
      ([
        week,
        count,
      ]) => ({ week, count }),
    );
  const isTotal = trendKey === 'total';
  const buckets = isTotal ? totalBuckets : weeks(trendKey);
  const trendLabel = isTotal
    ? t('cockpit_stats_total', 'Gesamt')
    : label(METRICS.find((m) => m.key === trendKey) ?? METRICS[0]);
  const sum = buckets.reduce((s, b) => s + b.count, 0);
  const peak = Math.max(0, ...buckets.map((b) => b.count));
  const first = buckets[0]?.week;
  const last = buckets[buckets.length - 1]?.week;
  const first0 = present[0];
  const online = wp[first0.key]?.onlineSinceDate;
  const changed = wp[first0.key]?.lastChangeDate;
  const rows = totalBuckets.map((b) => ({
    week: b.week,
    ...Object.fromEntries(
      present.map((m) => [
        m.key,
        weeks(m.key).find((x) => x.week === b.week)?.count ?? 0,
      ]),
    ),
    total: b.count,
  }));

  return (
    <V3Page>
      {head}
      <div
        className="grid g4"
        data-od-id="stat-tiles"
      >
        {present.map((m) => {
          const d = wp[m.key];
          const delta: number | undefined = d.changeInLastWeek;
          return (
            <div
              className="card kpi"
              key={m.key}
              data-od-id={m.od}
              data-widget={m.key}
            >
              <div className="ic">
                <Ico
                  name={m.icon}
                  size={18}
                />
              </div>
              <div className="n">{d.value ?? '–'}</div>
              <div className="l">{label(m)}</div>
              {delta != null ? (
                <div className={`d ${delta > 0 ? 'up' : 'flat'}`}>
                  {delta > 0 ? `+${delta}` : '±0'}{' '}
                  {t('cockpit_stats_last_week', 'in der letzten Woche')}
                </div>
              ) : (
                <div
                  className="xs mu"
                  style={{ marginTop: 6 }}
                >
                  {t('cockpit_stats_since_start', 'Gesamt seit Start')}
                </div>
              )}
            </div>
          );
        })}
        {online ? (
          <div
            className="card kpi"
            data-od-id="stat-online-since"
          >
            <div className="ic">
              <Ico
                name="cal"
                size={18}
              />
            </div>
            <div
              className="n"
              style={{ fontSize: '1.375rem', padding: '4px 0 2px' }}
            >
              {fmtDate(online)}
            </div>
            <div className="l">{t('cockpit_stats_online', 'Online seit')}</div>
            {changed ? (
              <div
                className="xs mu"
                style={{ marginTop: 6 }}
              >
                {t('cockpit_stats_changed', 'Letzte Änderung')}{' '}
                {fmtDate(changed)}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <section
        className="card"
        data-od-id="trend-chart"
        aria-labelledby="h-tr"
      >
        <div
          className="sec"
          style={{ marginBottom: 12 }}
        >
          <div>
            <h2 id="h-tr">
              {t('cockpit_stats_trend', 'Verlauf')} · {buckets.length}{' '}
              {t('cockpit_stats_weeks', 'Wochen')}
            </h2>
            <p className="xs mu">
              {t('cockpit_stats_weekly', 'Wöchentliche Werte')}
            </p>
          </div>
          <div
            className="tab"
            role="group"
            aria-label={t('cockpit_stats_pick', 'Kennzahl wählen')}
            data-od-id="trend-switch"
          >
            {[
              ...present.map(
                (m) =>
                  [
                    m.key,
                    label(m),
                  ] as const,
              ),
              [
                'total',
                t('cockpit_stats_total', 'Gesamt'),
              ] as const,
            ].map(
              ([
                k,
                l,
              ]) => (
                <button
                  key={k}
                  type="button"
                  className={k === trendKey ? 'on' : ''}
                  aria-pressed={k === trendKey}
                  onClick={() => setTrendKey(k)}
                >
                  {l}
                </button>
              ),
            )}
          </div>
        </div>
        {buckets.length ? (
          <>
            <LineChart
              title={`${trendLabel} ${t(
                'cockpit_stats_per_week',
                'pro Woche',
              )}, ${first} – ${last}: ${buckets
                .map((b) => b.count)
                .join(', ')}. ${t(
                'cockpit_stats_peak',
                'Höchstwert',
              )} ${peak}.`}
              values={buckets.map((b) => b.count)}
              labels={[
                fmtDate(weekStartDateString(first)),
                fmtDate(weekStartDateString(last)),
              ]}
            />
            <p
              className="xs mu"
              style={{ marginTop: 4 }}
            >
              {sum} {trendLabel} {t('cockpit_stats_in', 'in')} {buckets.length}{' '}
              {t('cockpit_stats_weeks', 'Wochen')} ·{' '}
              {t('cockpit_stats_peak', 'Höchstwert')} {peak}{' '}
              {t('cockpit_stats_per_week', 'pro Woche')}
            </p>
          </>
        ) : (
          <p className="sm mu">
            {t('cockpit_stats_empty', 'Noch keine Daten vorhanden.')}
          </p>
        )}
        {rows.length ? (
          <details
            className="vals"
            data-od-id="trend-values"
          >
            <summary>
              {t('cockpit_stats_table', 'Werte als Tabelle anzeigen')}
            </summary>
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('cockpit_stats_week', 'Woche')}</th>
                  {present.map((m) => (
                    <th
                      key={m.key}
                      className="r"
                    >
                      {label(m)}
                    </th>
                  ))}
                  <th className="r">{t('cockpit_stats_total', 'Gesamt')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.week}>
                    <td>{r.week}</td>
                    {present.map((m) => (
                      <td
                        key={m.key}
                        className="r num"
                      >
                        {String(r[m.key])}
                      </td>
                    ))}
                    <td className="r num">{r.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        ) : null}
      </section>
    </V3Page>
  );
}
